// 🔧 settings_categories.js
// ✅ Purpose: Admin-only category management page
// ✅ Features:
// - View all categories under /events/{eventId}/categories
// - Add new categories with numeric IDs starting at 1
// - Prevents use/display of category ID 0 and "init" placeholder category
// - Rename categories
// - Delete categories with confirmation
// - Admin/manager category import from another event
// - Uses standard admin layout
// - Shows success/error notifications at the top of the page
// - Auto-clears notifications after 20 seconds

import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  doc,
  query,
  setDoc,
  getDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import { useEventContext } from "../../hooks/use_event_context";
import { useRole } from "../../role_context";
import { useIsEventManager } from "../../hooks/use_is_event_manager";
import FullPageSpinner from "../../components/FullPageSpinner";
import { logger } from "../../utils/logger";
import { showStatus } from "../../utils/status_helpers";
import {
  getFunctions,
  httpsCallable,
} from "firebase/functions";

const SettingsCategories = () => {
  const { eventId, eventLoading, ready } = useEventContext({
    redirectIfNotFound: true,
  });

  const { role, eventRole, user } = useRole();
  const isManager = useIsEventManager(eventId);

  const [status, setStatus] = useState(null);
  const [categories, setCategories] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [allEvents, setAllEvents] = useState([]);
  const [importFromEventId, setImportFromEventId] = useState("");
  const [loadingCategories, setLoadingCategories] = useState(true);

  const isAdmin = role === "admin";
  const canImportCategories = role === "admin" || role === "manager";

  const canAccess =
    ["admin", "manager"].includes(role) ||
    eventRole === "manager" ||
    isManager;

  const functions = getFunctions();

  const manageCategory = httpsCallable(
    functions,
    "manageCategory"
  );
    
  useEffect(() => {
    if (!status?.message) return;

    const timer = setTimeout(() => {
      setStatus(null);
    }, 20000);

    return () => clearTimeout(timer);
  }, [status]);

  useEffect(() => {
    if (!eventId) return;

    loadCategories();

    if (canImportCategories && user?.uid) {
      loadOtherEvents();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, isAdmin, canImportCategories, user?.uid]);

  const getCategoryNameText = (value, fallback = "") => {
    if (value === null || value === undefined) {
      return fallback;
    }

    if (typeof value === "object") {
      return value.name || value.title || value.description || fallback;
    }

    return String(value);
  };

  const loadCategories = async () => {
    try {
      setLoadingCategories(true);

      logger.log("Loading categories inside standalone settings page...", {
        eventId,
      });

      const q = query(collection(db, "events", eventId, "categories"));
      const snap = await getDocs(q);

      const data = snap.docs
        .map((docSnap) => {
          const data = docSnap.data();

          return {
            id: docSnap.id,
            ...data,
            name: getCategoryNameText(data.name, ""),
          };
        })
        .filter((cat) => {
          const isInitCategory =
            getCategoryNameText(cat.name, "").toLowerCase() === "init";
          const isZeroCategory = cat.id === "0";

          return !isInitCategory && !isZeroCategory && !cat.isDefault;
        });

        const sorted = data.sort((a, b) => {
          const sortA =
            Number.isFinite(Number(a?.sortOrder))
              ? Number(a.sortOrder)
              : Number.isFinite(Number(a?.id))
              ? Number(a.id)
              : 9999;

          const sortB =
            Number.isFinite(Number(b?.sortOrder))
              ? Number(b.sortOrder)
              : Number.isFinite(Number(b?.id))
              ? Number(b.id)
              : 9999;

          if (sortA !== sortB) {
            return sortA - sortB;
          }

          return String(a?.name ?? "").localeCompare(String(b?.name ?? ""));
        });

      setCategories(sorted);
    } catch (err) {
      logger.error("Failed to load categories.", { value: err });

      showStatus(setStatus, {
        type: "error",
        message: "Unable to load categories.",
      });
    } finally {
      setLoadingCategories(false);
    }
  };

  const loadOtherEvents = async () => {
    try {
      const events = [];

      if (isAdmin) {
        const snapshot = await getDocs(collection(db, "events"));

        snapshot.docs.forEach((docSnap) => {
          const eventData = docSnap.data();

          if (docSnap.id === eventId || !eventData.name) {
            return;
          }

          events.push({
            id: docSnap.id,
            name: eventData.name,
          });
        });
      } else {
        const userSnap = await getDoc(doc(db, "users", user.uid));
        const userData = userSnap.exists() ? userSnap.data() : {};
        const eventInvites = userData.eventInvites || {};

        const managerEventIds = Object.keys(eventInvites).filter(
          (sourceEventId) => {
            const invite = eventInvites[sourceEventId];

            return (
              sourceEventId !== eventId &&
              invite?.role === "manager"
            );
          }
        );

        if (managerEventIds.length === 0) {
          const snapshot = await getDocs(collection(db, "events"));

          for (const docSnap of snapshot.docs) {
            if (docSnap.id === eventId) continue;

            const managerSnap = await getDoc(
              doc(db, "events", docSnap.id, "users", user.uid)
            );

            const managerData = managerSnap.exists()
              ? managerSnap.data()
              : null;

            if (managerData?.role === "manager") {
              const eventData = docSnap.data();

              if (eventData.name) {
                events.push({
                  id: docSnap.id,
                  name: eventData.name,
                });
              }
            }
          }
        }

        if (managerEventIds.length > 0) {
          for (const sourceEventId of managerEventIds) {
            const eventSnap = await getDoc(doc(db, "events", sourceEventId));

            if (!eventSnap.exists()) {
              continue;
            }

            const eventData = eventSnap.data();

            if (!eventData.name) {
              continue;
            }

            events.push({
              id: eventSnap.id,
              name: eventData.name,
            });
          }
        }
      }

      events.sort((a, b) =>
        String(a?.name ?? "").localeCompare(String(b?.name ?? ""))
      );

      setAllEvents(events);
    } catch (err) {
      logger.error("Failed to load importable events for category import.", {
        value: err,
      });

      showStatus(setStatus, {
        type: "error",
        message: "Unable to load available events.",
      });
    }
  };

  const handleImportCategories = async () => {
    if (!importFromEventId) return;

    const confirmImport = window.confirm(
      "Import categories from this event? Existing categories with the same ID may be updated."
    );

    if (!confirmImport) return;

    try {
      const sourceRef = collection(
        db,
        "events",
        importFromEventId,
        "categories"
      );

      const targetRef = collection(db, "events", eventId, "categories");
      const snapshot = await getDocs(sourceRef);

      const batchWrites = [];

      snapshot.forEach((docSnap) => {
        const data = docSnap.data();

        const cleanName = getCategoryNameText(data.name, "");

        if (docSnap.id === "0" || data.isDefault || cleanName.toLowerCase() === "init") {
          return;
        }

        const newDocRef = doc(targetRef, docSnap.id);

        batchWrites.push(
          setDoc(
            newDocRef,
            {
              ...data,
              name: cleanName,
              eventId,
            },
            { merge: true }
          )
        );
      });

      await Promise.all(batchWrites);

      setImportFromEventId("");
      await loadCategories();

      showStatus(setStatus, {
        type: "success",
        message: "Categories imported successfully.",
      });
    } catch (err) {
      logger.error("Failed to import categories.", { value: err });

      showStatus(setStatus, {
        type: "error",
        message: "Failed to import categories.",
      });
    }
  };

  const handleAddCategory = async () => {
    const name = newCategoryName.trim();

    if (!name) return;

    const exists = categories.some(
      (cat) =>
        getCategoryNameText(cat.name, "").toLowerCase() === name.toLowerCase()
    );

    if (exists) {
      showStatus(setStatus, {
        type: "warning",
        message: "Category already exists.",
      });

      return;
    }

    try {
      const catRef = collection(db, "events", eventId, "categories");
      const snap = await getDocs(catRef);

      const existingIds = snap.docs
        .map((docSnap) => parseInt(docSnap.id, 10))
        .filter((n) => Number.isInteger(n) && n !== 0);

      const nextId = existingIds.length
        ? Math.max(...existingIds) + 1
        : 1;

      // --------------------------------------------------
      // Modern category model:
      // - Firestore document ID is the stable identifier
      // - Preserve the legacy numeric id for compatibility
      // - sortOrder becomes the primary display ordering
      // --------------------------------------------------

      await manageCategory({
        eventId,
        action: "update",
        categoryId: String(nextId),
        category: {
          id: nextId,
          sortOrder: nextId,
          name,
        },
      });
      
      setNewCategoryName("");
      await loadCategories();

      showStatus(setStatus, {
        type: "success",
        message: "Category added successfully.",
      });
    } catch (err) {
      logger.error("Failed to add category.", { value: err });

      showStatus(setStatus, {
        type: "error",
        message: "Failed to save category.",
      });
    }
  };

  const handleRenameCategory = async (id, name) => {
    const cleanName = name.trim();

    if (!cleanName) {
      showStatus(setStatus, {
        type: "warning",
        message: "Category name cannot be blank.",
      });

      await loadCategories();
      return;
    }

    if (id === "0") {
      showStatus(setStatus, {
        type: "warning",
        message: "Category ID 0 cannot be edited.",
      });

      await loadCategories();
      return;
    }

    try {
      await manageCategory({
        eventId,

        action: "update",

        categoryId: id,

        category: {
          id: Number(id),
          name: cleanName,
        },
      });

      showStatus(setStatus, {
        type: "success",
        message: "Category saved successfully.",
      });

      await loadCategories();
    } catch (err) {
      logger.error("Failed to rename category.", { value: err });

      showStatus(setStatus, {
        type: "error",
        message: "Failed to save category.",
      });
    }
  };

  const handleDeleteCategory = async (id, name) => {
    if (id === "0") {
      showStatus(setStatus, {
        type: "warning",
        message: "Category ID 0 cannot be deleted.",
      });

      return;
    }

    const confirmDelete = window.confirm(
      `Are you sure you want to delete the category "${name}"?\n\nThis action cannot be undone.`
    );

    if (!confirmDelete) return;

    try {
      await manageCategory({
        eventId,

        action: "delete",

        categoryId: id,
      });
      await loadCategories();

      showStatus(setStatus, {
        type: "warning",
        message: "Category deleted.",
      });
    } catch (err) {
      logger.error("Failed to delete category.", { value: err });

      showStatus(setStatus, {
        type: "error",
        message: "Failed to delete category.",
      });
    }
  };

  const canImport = canImportCategories && allEvents.length > 0;

  if (!ready || eventLoading || isManager === null || loadingCategories) {
    return <FullPageSpinner message="Loading categories..." />;
  }

  if (!canAccess) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger">
          You do not have permission to access this page.
        </div>
      </div>
    );
  }

  return (
    <section id="top-content" className="container-fluid py-5">
      <div className="container" style={{ maxWidth: "1200px" }}>
        <div className="row mb-4">
          <div className="col-12 col-lg-8">
            <h1 className="display-5 mb-2">🏷 Categories</h1>
            <p className="lead text-muted mb-0">
              Categories are optional. Without categories, entries use an unnamed default category.
            </p>
          </div>

          <div className="col-12 col-lg-4 mt-3 mt-lg-0 d-flex justify-content-lg-end align-items-start">
            <button
              className="btn btn-success"
              onClick={handleAddCategory}
              disabled={!newCategoryName.trim()}
            >
              ➕ Add Category
            </button>
          </div>
        </div>

        {status?.message && (
          <div
            className={`alert ${
              status.type === "success"
                ? "alert-success"
                : status.type === "warning"
                ? "alert-warning"
                : status.type === "error"
                ? "alert-danger"
                : "alert-info"
            }`}
          >
            {status.message}
          </div>
        )}

        <div className="card shadow-sm mb-4">
          <div className="card-header bg-white">
            <strong>Category Tools</strong>
          </div>

          <div className="card-body">
            <div className="row g-4">
              <div className="col-12 col-lg-6">
                <label className="form-label">Add New Category</label>

                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Best Paint, Classic, Post Modern..."
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleAddCategory();
                      }
                    }}
                  />

                  <button
                    className="btn btn-success"
                    onClick={handleAddCategory}
                    disabled={!newCategoryName.trim()}
                  >
                    Add
                  </button>
                </div>

                <div className="small text-muted mt-2">
                  New categories automatically receive the next available numeric ID.
                </div>
              </div>

              {canImportCategories && (
                <div className="col-12 col-lg-6">
                  <label className="form-label">
                    Import Categories From Another Event
                  </label>

                  <div className="input-group">
                    <select
                      className="form-select"
                      value={importFromEventId}
                      onChange={(e) => setImportFromEventId(e.target.value)}
                    >
                      <option value="">Select event...</option>

                      {allEvents.map((event) => (
                        <option key={event.id} value={event.id}>
                          {event.name}
                        </option>
                      ))}
                    </select>

                    <button
                      className="btn btn-outline-primary"
                      disabled={!importFromEventId}
                      onClick={handleImportCategories}
                    >
                      Import
                    </button>
                  </div>

                  <div className="small text-muted mt-2">
                    Existing categories with the same ID may be updated.
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {!canImport && canImportCategories && (
          <div className="alert alert-info">
            No other manageable events were found for category import.
          </div>
        )}

        {categories.length === 0 ? (
          <div className="card shadow-sm">
            <div className="card-body text-center py-5">

              <h4 className="mb-3">
                🏷 No Categories Have Been Created
              </h4>

              <p className="text-muted mb-2">
                No category setup is required. Entries are automatically added to an unnamed default category.
              </p>

              <p className="text-muted mb-4">
                Categories group entries for voting and determine how results are displayed.
                Examples include <strong>Best Paint</strong>, <strong>Classic</strong>,
                <strong>Truck</strong>, or <strong>People's Choice</strong>.
              </p>

              <button
                className="btn btn-success"
                onClick={handleAddCategory}
                disabled={!newCategoryName.trim()}
              >
                ➕ Add Your First Category
              </button>

            </div>
          </div>
        ) : (
            <div className="card shadow-sm">
            <div className="card-header bg-white">
              <strong>Existing Categories</strong>
              <div className="small text-muted">
                Rename, save, or delete categories used for voting.
              </div>
            </div>

            <div className="card-body">
              <div className="row g-4">
                {categories.map((cat, index) => (
                  <div key={cat.id} className="col-12 col-md-6 col-xl-4">
                    <div className="card h-100 border shadow-sm">
                      <div className="card-body">
                        <label className="form-label small text-muted">
                          Category Name
                        </label>

                        <input
                          type="text"
                          className="form-control mb-3"
                          value={cat.name}
                          onChange={(e) => {
                            const updated = [...categories];
                            updated[index].name = e.target.value;
                            setCategories(updated);
                          }}
                        />

                        <div className="d-flex flex-wrap gap-2">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() =>
                              handleRenameCategory(cat.id, cat.name)
                            }
                          >
                            💾 Save
                          </button>

                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() =>
                              handleDeleteCategory(cat.id, cat.name)
                            }
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );

};

export default SettingsCategories;
